import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Breadcrumbs,
  Container,
  Grid,
  Link,
  Typography,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Alert,
  Button
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Link as RouterLink } from 'react-router-dom';
import ChevronRightIcon from '../../icons/ChevronRight';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { versionNotesApi } from '../../api/versionNotesApi';
import DescriptionIcon from '@mui/icons-material/Description';
import ArrowLeftIcon from '../../icons/ArrowLeft';
import { motion } from 'framer-motion';
import { alpha } from '@mui/material/styles';
import { toneAlpha, lightVeil, grey } from '../../theme/tones';

const AnimatedMeshBackground = () => (
    <Box sx={{ 
        position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, 
        overflow: 'hidden', zIndex: 0, opacity: 0.4,
        background: (theme) => `${theme.palette.background.default}`,
        pointerEvents: 'none'
    }}>
        <motion.div
            animate={{ x: [0, 120, -60, 0], y: [0, -120, 60, 0], scale: [1, 1.3, 0.8, 1] }}
            transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
            style={{ position: 'absolute', top: '-15%', left: '-10%', width: '45vw', height: '45vw', background: `radial-gradient(circle, ${toneAlpha('blue', 0.12)} 0%, ${lightVeil(0)} 70%)`, borderRadius: '50%', filter: 'blur(60px)' }}
        />
        <motion.div
            animate={{ x: [0, -100, 80, 0], y: [0, 100, -50, 0], scale: [1, 1.2, 0.9, 1] }}
            transition={{ duration: 35, repeat: Infinity, ease: "linear" }}
            style={{ position: 'absolute', bottom: '-25%', right: '-10%', width: '55vw', height: '55vw', background: `radial-gradient(circle, ${toneAlpha('emerald', 0.1)} 0%, ${lightVeil(0)} 70%)`, borderRadius: '50%', filter: 'blur(60px)' }}
        />
    </Box>
);

const StyledCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  '&:hover': {
    boxShadow: theme.shadows[4],
  },
}));

const VersionNotesPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [versionNotes, setVersionNotes] = useState([]);
  const [selectedNote, setSelectedNote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (id) {
      loadVersionNote(id);
    } else {
      setSelectedNote(null);
      loadVersionNotes();
    }
  }, [id]);

  const loadVersionNotes = async () => {
    try {
      setLoading(true);
      const response = await versionNotesApi.getPublishedVersionNotes();
      
      if (response.status === 200) {
        setVersionNotes(response.data);
      } else {
        setError('Erreur lors du chargement des notes de versions');
      }
    } catch (error) {
      console.error('Erreur:', error);
      setError('Erreur lors du chargement des notes de versions');
    } finally {
      setLoading(false);
    }
  };

  const loadVersionNote = async (noteId) => {
    try {
      setLoading(true);
      const response = await versionNotesApi.getVersionNoteById(noteId);
      
      if (response.status === 200) {
        setSelectedNote(response.data);
      } else {
        setError('Note de version non trouvée');
      }
    } catch (error) {
      console.error('Erreur:', error);
      setError('Erreur lors du chargement de la note de version');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  // Affichage d'une note spécifique
  if (selectedNote) {
    return (
      <>
        <Helmet>
          <title>{selectedNote.title} | Notes de versions | Syndays</title>
        </Helmet>
        
        <Box
          sx={{
            bgcolor: 'action.hover',
            minHeight: '100%',
            position: 'relative',
            overflow: 'hidden',
            pb: 10,
            pt: 4
          }}
        >
          <AnimatedMeshBackground />

          <Container maxWidth={false} disableGutters sx={{ px: 2, position: 'relative', zIndex: 1 }}>
            <Box sx={{ mb: 2 }}>
              <Button
                onClick={() => navigate('/dashboard/version-notes')}
                startIcon={<ArrowLeftIcon style={{ fontSize: 14 }} />}
                size="small"
                sx={{
                  color: 'text.secondary',
                  fontWeight: 800,
                  textTransform: 'none',
                  bgcolor: (theme) => alpha(theme.palette.common.black, 0.03),
                  borderRadius: '10px',
                  px: 2,
                  '&:hover': { bgcolor: (theme) => alpha(theme.palette.common.black, 0.06) }
                }}
              >
                Retour aux notes de versions
              </Button>
            </Box>

            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12}>
                <Box sx={{ 
                    p: 3, borderRadius: '24px', 
                    background: (theme) => alpha(theme.palette.common.white, 0.8), backdropFilter: 'blur(10px)', border: (theme) => `1px solid ${alpha(theme.palette.common.white, 0.9)}`,
                    boxShadow: (theme) => `0 4px 20px ${alpha(theme.palette.common.black, 0.03)}`,
                    display: 'flex', alignItems: 'center', gap: 3
                }}>
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: '16px',
                      background: (theme) => `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.dark} 100%)`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: (theme) => `0 8px 20px ${alpha(theme.palette.secondary.main, 0.3)}`,
                      flexShrink: 0,
                    }}
                  >
                    <DescriptionIcon sx={{ color: grey(0), fontSize: '1.7rem' }} />
                  </Box>
                  <Box>
                    <Breadcrumbs separator={<ChevronRightIcon fontSize="small" />} sx={{ mb: 1 }}>
                        <Link color="textPrimary" component={RouterLink} to="/dashboard" variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', textDecoration: 'none', '&:hover': { color: 'secondary.main' } }}>Dashboard</Link>
                        <Link color="textPrimary" component={RouterLink} to="/dashboard/version-notes" variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', textDecoration: 'none', '&:hover': { color: 'secondary.main' } }}>Notes de versions</Link>
                        <Typography color="textSecondary" variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em' }}>Détails</Typography>
                    </Breadcrumbs>
                    <Typography variant="h4" sx={{ fontWeight: 900, color: 'text.primary', letterSpacing: '-0.02em', mb: 0.5 }}>
                      {selectedNote.title}
                    </Typography>
                    <Box display="flex" alignItems="center" gap={2} mt={1}>
                      <Chip 
                        label={selectedNote.version_number} 
                        color="primary" 
                        size="small" 
                        sx={{ borderRadius: '8px', fontWeight: 800 }}
                      />
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                        Date de sortie : {formatDate(selectedNote.release_date)}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                        Par : {selectedNote.author_name}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Grid>
            </Grid>

            <Card sx={{ borderRadius: '24px', border: (theme) => `1px solid ${alpha(theme.palette.common.white, 0.9)}`, background: (theme) => alpha(theme.palette.common.white, 0.7), backdropFilter: 'blur(20px)', boxShadow: `0 8px 32px 0 ${toneAlpha('blue', 0.05)}`, overflow: 'hidden', p: 1 }}>
              <CardContent>
                <Box 
                  dangerouslySetInnerHTML={{ 
                    __html: selectedNote.content
                  }}
                />
              </CardContent>
            </Card>
          </Container>
        </Box>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Notes de versions | Syndays</title>
      </Helmet>
      
      <Box
        sx={{
          bgcolor: 'action.hover',
          minHeight: '100%',
          position: 'relative',
          overflow: 'hidden',
          pb: 10,
          pt: 4
        }}
      >
        <AnimatedMeshBackground />

        <Container maxWidth={false} disableGutters sx={{ px: 2, position: 'relative', zIndex: 1 }}>
          <Box sx={{ mb: 2 }}>
            <Button
              component={RouterLink}
              to="/dashboard"
              startIcon={<ArrowLeftIcon style={{ fontSize: 14 }} />}
              size="small"
              sx={{
                color: 'text.secondary',
                fontWeight: 800,
                textTransform: 'none',
                bgcolor: (theme) => alpha(theme.palette.common.black, 0.03),
                borderRadius: '10px',
                px: 2,
                '&:hover': { bgcolor: (theme) => alpha(theme.palette.common.black, 0.06) }
              }}
            >
              Retour
            </Button>
          </Box>

          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12}>
              <Box sx={{ 
                  p: 3, borderRadius: '24px', 
                  background: (theme) => alpha(theme.palette.common.white, 0.8), backdropFilter: 'blur(10px)', border: (theme) => `1px solid ${alpha(theme.palette.common.white, 0.9)}`,
                  boxShadow: (theme) => `0 4px 20px ${alpha(theme.palette.common.black, 0.03)}`,
                  display: 'flex', alignItems: 'center', gap: 3
              }}>
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: '16px',
                    background: (theme) => `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.dark} 100%)`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: (theme) => `0 8px 20px ${alpha(theme.palette.secondary.main, 0.3)}`,
                    flexShrink: 0,
                  }}
                >
                  <DescriptionIcon sx={{ color: grey(0), fontSize: '1.7rem' }} />
                </Box>
                <Box>
                  <Breadcrumbs separator={<ChevronRightIcon fontSize="small" />} sx={{ mb: 1 }}>
                      <Link color="textPrimary" component={RouterLink} to="/dashboard" variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', textDecoration: 'none', '&:hover': { color: 'secondary.main' } }}>Dashboard</Link>
                      <Typography color="textSecondary" variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em' }}>Notes de versions</Typography>
                  </Breadcrumbs>
                  <Typography variant="h4" sx={{ fontWeight: 900, color: 'text.primary', letterSpacing: '-0.02em', mb: 0.5 }}>
                      Notes de versions
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                      Suivez les nouveautés, améliorations et corrections apportées à la plateforme.
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>

          {error && (
            <Alert severity="error" sx={{ mt: 3 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ mt: 3 }}>
            {versionNotes.length === 0 ? (
              <Card>
                <CardContent>
                  <Typography variant="body1" color="textSecondary" align="center">
                    Aucune note de version publiée trouvée.
                  </Typography>
                </CardContent>
              </Card>
            ) : (
              versionNotes.map((note) => (
                <StyledCard key={note.id}>
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                      <Box flex={1}>
                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                          <Typography variant="h6" component="h2">
                            {note.title}
                          </Typography>
                          <Chip 
                            label={note.version_number} 
                            color="primary" 
                            size="small" 
                          />
                        </Box>
                        
                        <Typography variant="body2" color="textSecondary" gutterBottom>
                          Date de sortie : {formatDate(note.release_date)}
                        </Typography>
                        
                        <Typography variant="body2" color="textSecondary" gutterBottom>
                          Par : {note.author_name}
                        </Typography>
                        
                        <Box 
                          sx={{ 
                            mt: 2,
                            maxHeight: 200,
                            overflow: 'hidden'
                          }}
                          dangerouslySetInnerHTML={{ 
                            __html: note.content
                          }}
                        />
                        
                        <Button
                          component={RouterLink}
                          to={`/dashboard/version-notes/${note.id}`}
                          variant="outlined"
                          size="small"
                          sx={{ mt: 2 }}
                        >
                          Lire la suite
                        </Button>
                      </Box>
                    </Box>
                  </CardContent>
                </StyledCard>
              ))
            )}
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default VersionNotesPage; 